// custom override of apollo useQuery to integrate with error handling in ErrorManager

import {
  useQuery,
  QueryHookOptions,
  QueryResult,
  ApolloError,
  WatchQueryFetchPolicy,
} from "@apollo/client";
import { useHandleError } from "../contexts/ErrorManager/ErrorManager";
import { DocumentNode } from "graphql";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useGetPageFromPath } from "./customHooks/genericHooks";
import { useMemo } from "react";
import { getIsCypressRunning } from "constants/constants";

const useQueryWithErrorHandling = (
  queryName: string,
  query: DocumentNode,
  options: QueryHookOptions<any, Record<string, any>> | undefined
): QueryResult<any, Record<string, any>> => {
  const isStorybook = useIsStorybook();

  const handleErr = useHandleError();
  const extraQueryOptions = useExtraQueryOptions();

  const { refetch, ...rest } = useQuery(query, {
    ...options,
    ...extraQueryOptions,
    displayName: `Query_${queryName}`,
    pollInterval: 0,
    onCompleted: (resp: any) => {
      return options?.onCompleted ? options?.onCompleted(resp) : null;
    },
    errorPolicy: "all", // show all GraphQL errors that appear alongside data response (doesn't show up otherwise)
    skip: options?.skip || isStorybook,
    notifyOnNetworkStatusChange: true,
    onError: (err: ApolloError) => {
      handleErr(err, { ...(options?.variables || {}), queryName });
      return options?.onError ? options.onError(err) : null;
    },
  });

  return { refetch, ...rest };
};

export default useQueryWithErrorHandling;

export function useExtraQueryOptions(): {
  fetchPolicy?: WatchQueryFetchPolicy;
} {
  const { isTrendsPage } = useGetPageFromPath();
  return useMemo(
    () =>
      isTrendsPage && !getIsCypressRunning()
        ? // set the fetchPolicy:
          // "network-only" = no cache, always request
          // "cache-first" = look in cache, if it's there, don't request => avoid using this on long-running pages
          // "cache-and-network" = "stale-while-revalidate" = look in cache, if it's there, use it and
          //    meanwhile send a request, if the data comes back different, use the new data
          //
          // Trends page is not a long-running page, so we don't need to revalidate...
          // but, if we start to use this on any long-running pages (like timeline, hublet pages),
          // consider adding a useInterval to change "cache-first" to "cache-and-network" and back every 30s or so
          {
            fetchPolicy: "network-only", // Used for first execution
            nextFetchPolicy: "cache-first", // Used for subsequent executions
          }
        : {},
    [isTrendsPage]
  );
}
